import { useState, useCallback } from 'react';
import { Dialog, Button, Flex, Text, FileUpload, FileInfo, FileList, useToasts } from '@applyboard/crystal-ui';
import { PlusOutlineIcon } from '@applyboard/ui-icons';
import { EducationHistoryEditData } from './EducationHistoryEditFields';
import { CreateEducationHistoryFields } from './CreateEducationHistoryFields';
import { validateEducationHistory } from '../educationHistory.validation';
import { useCreateEducationHistory, CreateEducationHistoryParams } from '../../../../data/useCreateEducationHistory';
import { useTranscriptAiEnabled, useGradeSchemeRequired } from '../../../../data/useGetCustomizedDocumentTypes';

const EMPTY_FORM: EducationHistoryEditData = {
  schoolName: '',
  educationLevel: '',
  educationCountry: '',
  credentialReceived: '',
  fieldOfStudy: '',
  degree: '',
  gradeSchemeId: '',
  gradeSchemeName: '',
  startDate: '',
  endDate: '',
};

const ALLOWED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png'] as const;

type AddEducationHistoryDialogProps = Readonly<{
  applicationId: string;
  studentId: string;
}>;

export function AddEducationHistoryDialog({ applicationId, studentId }: AddEducationHistoryDialogProps) {
  const toast = useToasts();
  const { createEducationHistory, isCreatingEducationHistory } = useCreateEducationHistory();
  const transcriptAiEnabled = useTranscriptAiEnabled();
  const gradeSchemeRequired = useGradeSchemeRequired();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<EducationHistoryEditData>({ ...EMPTY_FORM });
  const [transcriptFile, setTranscriptFile] = useState<FileInfo | null>(null);
  const [certificateFile, setCertificateFile] = useState<FileInfo | null>(null);

  const resetForm = useCallback(() => {
    setFormData({ ...EMPTY_FORM });
    setTranscriptFile(null);
    setCertificateFile(null);
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        resetForm();
      }
    },
    [resetForm]
  );

  const handleFieldChange = useCallback((field: keyof EducationHistoryEditData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const validation = validateEducationHistory(formData);
    if (!validation.isValid && validation.errors) {
      validation.errors.forEach((error) => {
        toast.negative(new Error(error));
      });
      return;
    }

    const params: CreateEducationHistoryParams = {
      studentId,
      applicationId,
      schoolName: formData.schoolName,
      educationLevel: formData.educationLevel,
      educationCountry: formData.educationCountry,
      credentialReceived: formData.credentialReceived,
      startDate: formData.startDate,
      endDate: formData.endDate,
      ...(formData.gradeSchemeId && { gradeSchemeId: formData.gradeSchemeId }),
      ...(formData.gradeSchemeName && { gradeSchemeName: formData.gradeSchemeName }),
    };

    if (transcriptFile) {
      params.transcriptFile = {
        fileObject: transcriptFile.file,
        fileName: transcriptFile.file.name,
        contentType: transcriptFile.file.type,
      };
    }

    if (certificateFile) {
      params.certificateFile = {
        fileObject: certificateFile.file,
        fileName: certificateFile.file.name,
        contentType: certificateFile.file.type,
      };
    }

    try {
      await createEducationHistory(params);
      toast.positive('Education history added successfully');
      handleOpenChange(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add education history';
      toast.negative(new Error(errorMessage));
    }
  }, [
    formData,
    transcriptFile,
    certificateFile,
    studentId,
    applicationId,
    createEducationHistory,
    toast,
    handleOpenChange,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} size="lg">
      <Dialog.Trigger>
        <Button width="fill" size="md" intent="secondary" emphasis="outlined" leadIcon={PlusOutlineIcon}>
          Add another education experience
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>Add Education History</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={8}>
          <CreateEducationHistoryFields
            editedData={formData}
            onChange={handleFieldChange}
            gradeScheme={{ enabled: transcriptAiEnabled, required: gradeSchemeRequired }}
          />

          <Flex direction="column" gap={4}>
            <Text variant="titleS">Supporting Documents (Optional)</Text>
            <Flex direction="column" gap={2} grow={1}>
              <SingleFileUpload label="Transcript" file={transcriptFile} onChange={setTranscriptFile} />
            </Flex>
            <Flex direction="column" gap={2} grow={1}>
              <SingleFileUpload label="Certificate" file={certificateFile} onChange={setCertificateFile} />
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          width="fill"
          intent="secondary"
          emphasis="outlined"
          onClick={() => handleOpenChange(false)}
          disabled={isCreatingEducationHistory}
        >
          Cancel
        </Button>
        <Button
          width="fill"
          intent="primary"
          emphasis="contained"
          onClick={handleSave}
          loading={isCreatingEducationHistory}
          disabled={isCreatingEducationHistory}
        >
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

type SingleFileUploadProps = Readonly<{
  label: string;
  file: FileInfo | null;
  onChange: (file: FileInfo | null) => void;
}>;

function SingleFileUpload({ label, file, onChange }: SingleFileUploadProps) {
  const handleChange = useCallback(
    (value: FileInfo[]) => {
      const newFile = value.find((item) => 'file' in item && item.file) ?? null;
      onChange(newFile);
    },
    [onChange]
  );

  if (file) {
    return (
      <Flex direction="column" gap={2}>
        <Text variant="labelS">{label}</Text>
        <FileList orientation="vertical" onRemove={() => onChange(null)}>
          <FileList.Item value={file.id} name={file.file.name} />
        </FileList>
      </Flex>
    );
  }

  return (
    <FileUpload
      allowedFileTypes={[...ALLOWED_FILE_TYPES]}
      maxFileSize={0}
      label={label}
      onChange={handleChange}
      size="lg"
      onRemoveFile={() => onChange(null)}
      value={[]}
    >
      <FileUpload.Button>Browse Files</FileUpload.Button>
    </FileUpload>
  );
}
